import React from "react";
import { Link } from "react-router-dom";
import { Check } from "@phosphor-icons/react";
import { SERVICES, SERVICE_GROUPS, PACKAGE_ONLY_SLUGS, findService } from "../../data/services";

export const REASONS = SERVICES.filter((s) => !PACKAGE_ONLY_SLUGS.includes(s.slug)).map((s) => ({ slug: s.slug, title: s.title }));

export const reasonTitles = (slugs) => REASONS.filter((r) => slugs.includes(r.slug)).map((r) => r.title);

const GROUP_LABELS = { vascular: "Specialised vascular measurements" };

const Reason = ({ slug, on, toggle }) => (
  <label data-testid={`reason-${slug}`}
    className={`flex items-center gap-3 px-3 py-2.5 rounded-sm border cursor-pointer select-none transition-colors ${on ? "border-bronze-dark bg-cream-100" : "border-stone-200 hover:border-bronze-light"}`}>
    <input type="checkbox" className="sr-only" checked={on} onChange={() => toggle(slug)} data-testid={`reason-checkbox-${slug}`} />
    <span className={`h-5 w-5 shrink-0 rounded-sm border flex items-center justify-center ${on ? "bg-bronze-dark border-bronze-dark text-white" : "border-stone-300 bg-white"}`}>
      {on && <Check size={13} weight="bold" />}
    </span>
    <span className="text-sm flex-1">{findService(slug).title}</span>
  </label>
);

const BOOKABLE_GROUPS = SERVICE_GROUPS.filter((g) => !g.packageOnly);

export const ReasonPicker = ({ value, onChange, exclude = [], step = 2, testsOnly = false }) => {
  const toggle = (slug) => onChange(value.includes(slug) ? value.filter((s) => s !== slug) : [...value, slug]);
  return (
    <fieldset data-testid="reason-picker" className="bg-white border border-bronze-light/30 rounded-md p-6">
      <legend className="sr-only">What are you coming to see us about?</legend>
      <div className="eyebrow">Step {step}</div>
      <h2 className="font-serif text-2xl mt-1">{testsOnly ? "Which test would you like?" : "What would you like to see us about?"}</h2>
      <p className="text-sm text-ink-muted mt-1">{testsOnly ? "Echo and 24-hour blood pressure monitoring can be booked on their own." : "Tick at least one - it helps your consultant prepare."}</p>
      {BOOKABLE_GROUPS.filter((g) => g.slugs.some((slug) => !exclude.includes(slug))).map((g) => (
        <div key={g.key} data-testid={`reason-group-${g.key}`} className="mt-5">
          <div className="text-xs uppercase tracking-[0.16em] text-bronze-dark">{GROUP_LABELS[g.key] || g.title}</div>
          <div className="mt-2 grid sm:grid-cols-2 gap-2">
            {g.slugs.filter((slug) => !exclude.includes(slug)).map((slug) => <Reason key={slug} slug={slug} on={value.includes(slug)} toggle={toggle} />)}
          </div>
        </div>
      ))}
      <p data-testid="reason-tests-note" className="mt-5 text-xs text-ink-muted">
        Tests are added to your consultation fee and done just before it, in the same visit - by our nurse (ECG, ABPM, Holter, bioimpedance) or our echo physiologist Navazh Jalaludeen (echo). Your consultant then sees you for the consultation. Echo and ABPM can also be booked on their own - just tick them without anything else. ECG, Holter monitoring and bioimpedance are booked with a consultation. The full price is shown on the secure payment page before you pay.
      </p>
      <p data-testid="reason-vascular-note" className="mt-5 text-xs text-ink-muted">
        Specialised vascular measurements (cIMT, arterial stiffness and FMD) aren&apos;t booked here - they&apos;re offered as separately priced packages. Please <Link to="/contact" className="text-bronze-dark underline underline-offset-2">contact us</Link> if you&apos;re interested.
      </p>
    </fieldset>
  );
};
